import { BackofficeMetricUnit } from '@backoffice-frontend/graphql'
import { useMetricTracking } from '@backoffice-frontend/tracking'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingLivestream } from '../components/LoadingLivestream'
import type { VideoWallTile } from './types'
import { useVideoTiles } from './useVideoTiles'
import {
  CABIN_CAMERA_FOCUSED_ORDER,
  CABIN_CAMERA_GROUPED_NUMBERS,
  CABIN_CAMERA_PREVIEW_HIDDEN_NUMBERS,
  FRONT_CAMERA_FOCUSED_ORDER,
  FRONT_CAMERA_PREVIEW_HIDDEN_NUMBERS,
  FRONT_CAMERA_PREVIEW_NUMBER,
  getFocusedTiles,
} from './utils'
import { NoVideoTile, PreviewTile, VideoTile } from './VideoTile'

const TILE_TIMEOUT_MS = 10_000
const PREVIEW_TILE_WIDTH = 140
const FOCUSED_TILE_MAX_HEIGHT = 400
const FOCUSED_TILE_COMPACT_QUERY = '@container (max-width: 900px)'

export const VideoWallCockpitView = ({ vehicleId }: { vehicleId: string }) => {
  const { t } = useTranslation()
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([])
  const previewRefs = useRef<Array<HTMLCanvasElement | null>>([])
  const tiles = useVideoTiles()
  const [focusedTile, setFocusedTile] = useState(tiles.at(0))
  const [timedOut, setTimedOut] = useState(false)

  // Chime may need a moment to attach remote video tiles; only show retry after a short grace period.
  useEffect(() => {
    if (tiles.length > 0 || timedOut) return
    const id = setTimeout(() => setTimedOut(true), TILE_TIMEOUT_MS)
    return () => clearTimeout(id)
  }, [tiles.length, timedOut])
  const { trackMetric } = useMetricTracking()

  const cameraSelectionStartTime = useRef<number | null>(null)
  const lastSelectedCamera = useRef<string | null>(null)

  const handleCameraSelection = async (
    tile: VideoWallTile,
    selectionType: 'manual' | 'automatic' = 'manual',
  ) => {
    if (lastSelectedCamera.current && cameraSelectionStartTime.current) {
      const selectionDuration = Date.now() - cameraSelectionStartTime.current
      await trackMetric({
        metricName: 'CameraSelectionTime',
        value: selectionDuration,
        unit: BackofficeMetricUnit.Milliseconds,
        dimensions: [
          { name: 'cameraName', value: lastSelectedCamera.current },
          { name: 'vehicleId', value: vehicleId },
          { name: 'tileId', value: String(focusedTile?.id ?? -1) },
          { name: 'selectionType', value: selectionType },
        ],
        namespace: 'FleetControl/ad-operator-video',
      })
    }

    setFocusedTile(tile)
    lastSelectedCamera.current = tile.name
    cameraSelectionStartTime.current = Date.now()
  }

  // Previews use canvases because each Chime tile is already bound to a single video element.
  useEffect(() => {
    const videos = videoRefs.current
    const previews = previewRefs.current
    if (!videos || !previews) return

    const cleanups: Array<() => void> = []

    for (const tile of tiles) {
      const video = videos[tile.id]
      const preview = previews[tile.id]
      if (!video || !preview) continue

      const context = preview.getContext('2d')
      if (!context) continue

      let animationFrameId: number | undefined

      const drawPreview = () => {
        if (video.ended) return
        const width = video.videoWidth
        const height = video.videoHeight
        if (width === 0 || height === 0) return

        if (preview.width !== width) preview.width = width
        if (preview.height !== height) preview.height = height

        context.drawImage(video, 0, 0, width, height)
        animationFrameId = requestAnimationFrame(drawPreview)
      }

      const startDrawing = () => {
        if (animationFrameId !== undefined) return
        drawPreview()
      }

      if (video.videoWidth > 0 && video.videoHeight > 0) {
        startDrawing()
      } else {
        video.addEventListener('loadedmetadata', startDrawing)
        video.addEventListener('canplay', startDrawing)
      }

      cleanups.push(() => {
        video.removeEventListener('loadedmetadata', startDrawing)
        video.removeEventListener('canplay', startDrawing)
        if (animationFrameId !== undefined) {
          cancelAnimationFrame(animationFrameId)
        }
      })
    }

    return () => {
      cleanups.forEach(cleanup => {
        cleanup()
      })
    }
  }, [tiles])

  // Streams can appear or disappear while reconnecting, so keep the focused tile available and track automatic switches.
  useEffect(() => {
    const trackCameraChange = (
      newTile: { id: number; name: string } | undefined,
    ) => {
      if (lastSelectedCamera.current && cameraSelectionStartTime.current) {
        const selectionDuration = Date.now() - cameraSelectionStartTime.current
        trackMetric({
          metricName: 'CameraSelectionTime',
          value: selectionDuration,
          unit: BackofficeMetricUnit.Milliseconds,
          dimensions: [
            { name: 'cameraName', value: lastSelectedCamera.current },
            { name: 'vehicleId', value: vehicleId },
            { name: 'selectionType', value: 'automatic' },
          ],
          namespace: 'FleetControl/ad-operator-video',
        })
      }

      if (newTile) {
        lastSelectedCamera.current = newTile.name
        cameraSelectionStartTime.current = Date.now()
      } else {
        lastSelectedCamera.current = null
        cameraSelectionStartTime.current = null
      }
    }

    if (focusedTile && tiles.some(tile => tile.id === focusedTile.id)) {
      return
    }

    // focused tile set but not available anymore -> update or remove
    if (focusedTile && !tiles.some(tile => tile.id === focusedTile.id)) {
      const newTile = tiles.length > 0 ? tiles.at(0) : undefined
      trackCameraChange(newTile)
      setFocusedTile(newTile)
      return
    }

    // focused tile NOT set and tiles available -> set
    if (focusedTile === undefined && tiles.length > 0) {
      const newTile = tiles.at(0)
      trackCameraChange(newTile)
      setFocusedTile(newTile)
    }
  }, [tiles, focusedTile, trackMetric, vehicleId])

  // Flush the final camera view duration on unmount so the last selection is included in tracking.
  useEffect(() => {
    return () => {
      if (lastSelectedCamera.current && cameraSelectionStartTime.current) {
        const selectionDuration = Date.now() - cameraSelectionStartTime.current
        trackMetric({
          metricName: 'CameraSelectionTime',
          value: selectionDuration,
          unit: BackofficeMetricUnit.Milliseconds,
          dimensions: [
            { name: 'cameraName', value: lastSelectedCamera.current },
            { name: 'vehicleId', value: vehicleId },
            { name: 'sessionEnd', value: '1' },
          ],
          namespace: 'FleetControl/ad-operator-video',
        })
      }
    }
  }, [trackMetric, vehicleId])

  if (!focusedTile) {
    return timedOut ? (
      <NoVideoTile onRetry={() => setTimedOut(false)} />
    ) : (
      <LoadingLivestream />
    )
  }

  const focusedTiles = getFocusedTiles(focusedTile, tiles)
  const compactFocusedTiles =
    focusedTile.number === FRONT_CAMERA_PREVIEW_NUMBER ||
    CABIN_CAMERA_GROUPED_NUMBERS.has(focusedTile.number)
      ? [focusedTile]
      : focusedTiles

  const isGroupedFrontView = focusedTile.number === FRONT_CAMERA_PREVIEW_NUMBER
  const isGroupedCabinView = CABIN_CAMERA_GROUPED_NUMBERS.has(
    focusedTile.number,
  )
  const isGroupedView = isGroupedFrontView || isGroupedCabinView
  const groupOrder = isGroupedFrontView
    ? FRONT_CAMERA_FOCUSED_ORDER
    : isGroupedCabinView
      ? CABIN_CAMERA_FOCUSED_ORDER
      : []
  const selectableTileCount = tiles.filter(
    tile =>
      !FRONT_CAMERA_PREVIEW_HIDDEN_NUMBERS.has(tile.number) &&
      !CABIN_CAMERA_PREVIEW_HIDDEN_NUMBERS.has(tile.number),
  ).length
  const isSingleCamera = selectableTileCount <= 1
  const isSingleGroupedTile = isGroupedView && focusedTiles.length === 1
  const noRoundedCorners =
    isSingleCamera || isSingleGroupedTile || focusedTile.name === 'Rear'
  const missingSlots =
    isGroupedView && focusedTiles.length > 1
      ? groupOrder
          .map((cameraNumber, index) => ({ cameraNumber, index }))
          .filter(
            slot =>
              !focusedTiles.some(tile => tile.number === slot.cameraNumber),
          )
      : []

  return (
    <div
      css={theme => ({
        containerType: 'inline-size',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.space.Space1,
      })}
    >
      {/* Focused video */}
      <div
        css={theme => ({
          display: 'grid',
          ...(isGroupedView && !isSingleGroupedTile
            ? { gridTemplateColumns: `repeat(${groupOrder.length}, 1fr)` }
            : { gridAutoFlow: 'column', justifyContent: 'center' }),
          gap: theme.space.Space1,
          height: FOCUSED_TILE_MAX_HEIGHT,
          position: 'relative',
          backgroundColor: noRoundedCorners
            ? theme.semantic.ColorSurfaceReduced
            : 'unset',
          borderRadius: noRoundedCorners ? 0 : theme.space.Space2,
          overflow: 'hidden',
          [FOCUSED_TILE_COMPACT_QUERY]: {
            gridTemplateColumns: '1fr',
            gridAutoFlow: 'unset',
            height: 'unset',
            borderRadius: 0,
            backgroundColor: theme.semantic.ColorSurfaceReduced,
          },
        })}
      >
        {missingSlots.map(slot => (
          <div
            key={`missing-${slot.cameraNumber}`}
            css={theme => ({
              gridColumn: slot.index + 1,
              gridRow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: FOCUSED_TILE_MAX_HEIGHT,
              borderRadius: noRoundedCorners ? 0 : theme.space.Space2,
              color: theme.semantic.ColorContentReduced,
              backgroundColor: theme.semantic.ColorSurfaceReduced,
              textAlign: 'center',
              padding: theme.space.Space2,
              [FOCUSED_TILE_COMPACT_QUERY]: {
                display: 'none',
              },
            })}
          >
            {t('adFleetMap_videoStreamCameraNotAvailable_label')}
          </div>
        ))}

        {tiles.map(tile => {
          const focusedTileIndex = isGroupedView
            ? groupOrder.indexOf(tile.number)
            : focusedTiles.findIndex(ft => ft.id === tile.id)
          const isFocusedInMainView = focusedTileIndex !== -1
          const compactFocusedTileIndex = compactFocusedTiles.findIndex(
            ft => ft.id === tile.id,
          )
          const isFocusedInCompactView = compactFocusedTileIndex !== -1

          return (
            <div
              key={tile.id}
              css={theme => ({
                backgroundColor: theme.semantic.ColorSurfaceReduced,
                visibility: isFocusedInMainView ? 'visible' : 'hidden',
                gridColumn: isFocusedInMainView ? focusedTileIndex + 1 : 1,
                gridRow: 1,
                position: isFocusedInMainView ? 'relative' : 'absolute',
                height: FOCUSED_TILE_MAX_HEIGHT,
                borderRadius: noRoundedCorners ? 0 : theme.space.Space2,
                overflow: 'hidden',
                pointerEvents: isFocusedInMainView ? undefined : 'none',
                [FOCUSED_TILE_COMPACT_QUERY]: {
                  visibility: isFocusedInCompactView ? 'visible' : 'hidden',
                  gridColumn: isFocusedInCompactView
                    ? compactFocusedTileIndex + 1
                    : 1,
                  position: isFocusedInCompactView ? 'relative' : 'absolute',
                  width: isFocusedInCompactView ? '100%' : 1,
                  height: isFocusedInCompactView ? 'auto' : 1,
                  borderRadius: 0,
                  pointerEvents: isFocusedInCompactView ? undefined : 'none',
                },
                '& video': {
                  borderRadius: noRoundedCorners ? 0 : theme.space.Space2,
                  height: '100%',
                  width: '100%',
                  objectFit: 'contain',
                  [FOCUSED_TILE_COMPACT_QUERY]: {
                    borderRadius: 0,
                    height: 'auto',
                    width: '100%',
                  },
                },
                '& > div': {
                  alignItems: 'center',
                  justifyContent: 'center',
                },
              })}
            >
              <VideoTile
                ref={_ref => {
                  videoRefs.current[tile.id] = _ref
                }}
                tileId={tile.id}
                name={tile.name}
              />
            </div>
          )
        })}
      </div>

      {/* Previews — hidden when only one selectable camera is available */}
      {!isSingleCamera && (
        <div
          css={theme => ({
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: theme.space.Space1,
            [FOCUSED_TILE_COMPACT_QUERY]: {
              justifyContent: 'flex-start',
            },
          })}
        >
          {tiles.map(tile => {
            const isFocused = tile.id === focusedTile.id
            const isHiddenWide =
              FRONT_CAMERA_PREVIEW_HIDDEN_NUMBERS.has(tile.number) ||
              CABIN_CAMERA_PREVIEW_HIDDEN_NUMBERS.has(tile.number)
            return (
              <div
                key={`preview-${tile.id}`}
                css={theme => ({
                  display: isHiddenWide ? 'none' : 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.semantic.ColorSurfaceReduced,
                  overflow: 'hidden',
                  borderRadius: theme.space.Space2,
                  width: PREVIEW_TILE_WIDTH,
                  flex: '0 0 auto',
                  aspectRatio: '16 / 10',
                  cursor: 'pointer',
                  boxShadow: isFocused
                    ? `inset 0 0 0 4px ${theme.semantic.ColorContentDefault}`
                    : 'none',
                  [FOCUSED_TILE_COMPACT_QUERY]: {
                    display: 'flex',
                  },
                })}
                onClick={() => handleCameraSelection(tile, 'manual')}
              >
                <PreviewTile
                  ref={_ref => {
                    previewRefs.current[tile.id] = _ref
                  }}
                  name={tile.name}
                  isFocused={isFocused}
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
